import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  addOrganizationMember,
  createUser,
  DerinOrganization,
  DerinUser,
  MembershipRequest,
  searchUserByEmail,
} from "../../../api/derin";
import ModalShell from "../CollectionsTab/ModalShell";
import { collectionColors, fieldLabelSx, font, inputSx, subtitleSx } from "../CollectionsTab/tokens";
import { errorMessageOf } from "../errorMessage";
import { useToast } from "../Toast";
import OrganizationAccessRows, { emptyRow } from "./OrganizationAccessRows";
import { initialsOf, invalidFieldSx, isValidEmail, organizationChipSx, warningNoticeSx } from "./styles";

type Mode = "find" | "create";

type Search =
  | { status: "idle" }
  | { status: "searching" }
  | { status: "found"; user: DerinUser }
  | { status: "not-found" }
  | { status: "error"; message: string };

const SEARCH_DEBOUNCE_MS = 400;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Active organizations, for the organization selects. */
  organizations: DerinOrganization[];
  /** Adding from an organization's page: access is to that organization only. */
  scopedOrganization?: DerinOrganization;
  /** Called after users or memberships changed, so the caller can refetch. */
  onSaved: () => void;
}

/**
 * Adds someone to one or more organizations, either by finding their existing account
 * by email (which only creates memberships, never a second account) or by creating a
 * new one. One account per email is the whole point: a person working with several
 * clients keeps a single login.
 */
const AddUserModal: React.FC<Props> = ({ open, onClose, organizations, scopedOrganization, onSaved }) => {
  const { showSuccess, showError } = useToast();
  const [mode, setMode] = useState<Mode>("find");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [search, setSearch] = useState<Search>({ status: "idle" });
  const [rows, setRows] = useState<MembershipRequest[]>([emptyRow(scopedOrganization?.id)]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const foundUser = mode === "find" && search.status === "found" ? search.user : null;
  // Creating with an email that already has an account would only be refused.
  const registeredUser = mode === "create" && search.status === "found" ? search.user : null;
  const existingOrganizationIds = useMemo(
    () => foundUser?.memberships.map((m) => m.organizationId) ?? [],
    [foundUser]
  );
  const alreadyInScopedOrganization =
    !!scopedOrganization && existingOrganizationIds.includes(scopedOrganization.id);

  // Look the email up once the admin stops typing: to find the account, or, when
  // creating, to catch an email that is already registered.
  useEffect(() => {
    if (!isValidEmail(email)) {
      setSearch({ status: "idle" });
      return;
    }
    let cancelled = false;
    setSearch({ status: "searching" });
    const timer = setTimeout(async () => {
      try {
        const user = await searchUserByEmail(email.trim());
        if (!cancelled) setSearch(user ? { status: "found", user } : { status: "not-found" });
      } catch (err: any) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setSearch({ status: "not-found" });
        } else {
          setSearch({ status: "error", message: errorMessageOf(err) });
        }
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [email, mode]);

  // A newly found user may already be in an organization picked in a row.
  useEffect(() => {
    if (scopedOrganization) return;
    setRows((current) => {
      const kept = current.filter((r) => !existingOrganizationIds.includes(r.organizationId));
      return kept.length > 0 ? kept : [emptyRow()];
    });
  }, [existingOrganizationIds, scopedOrganization]);

  const switchMode = (next: Mode) => {
    if (next === mode) return;
    setMode(next);
    setSearch({ status: "idle" });
    setRows([emptyRow(scopedOrganization?.id)]);
  };

  const rowsComplete = rows.length > 0 && rows.every((r) => r.organizationId);
  const canSubmit =
    mode === "find"
      ? !!foundUser && rowsComplete && !alreadyInScopedOrganization
      : firstName.trim().length > 0 &&
        lastName.trim().length > 0 &&
        isValidEmail(email) &&
        !registeredUser &&
        search.status !== "searching" &&
        rowsComplete;

  const fullName = `${firstName.trim()} ${lastName.trim()}`;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (mode === "find" && foundUser) {
        for (const row of rows) {
          await addOrganizationMember(row.organizationId, foundUser.id, row.role);
        }
        showSuccess(`${foundUser.firstName} ${foundUser.lastName} was added`);
      } else {
        // No Survey API participant any more: nothing reads a user's participant
        // id, and creating one first left an orphan whenever the user was refused.
        await createUser({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          memberships: rows,
        });
        showSuccess(`${fullName} was created and will receive an activation email`);
      }
      onSaved();
      onClose();
    } catch (err) {
      showError(errorMessageOf(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleButton = (value: Mode, label: string) => (
    <Box
      component="button"
      type="button"
      onClick={() => switchMode(value)}
      aria-pressed={mode === value}
      sx={{
        flex: 1,
        height: "32px",
        border: "none",
        borderRadius: "10px",
        cursor: "pointer",
        fontFamily: font,
        fontWeight: 500,
        fontSize: "14px",
        color: collectionColors.textPrimary,
        background: mode === value ? collectionColors.white : "transparent",
        boxShadow: mode === value ? "0px 1px 3px rgba(0, 0, 0, 0.1)" : "none",
      }}
    >
      {label}
    </Box>
  );

  const textField = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    placeholder: string,
    type = "text",
    invalid = false
  ) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
      <Typography component="label" sx={fieldLabelSx}>
        {label}
        <Box
          component="input"
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          aria-invalid={invalid || undefined}
          sx={{ ...inputSx, display: "block", marginTop: "8px", ...(invalid ? invalidFieldSx : {}) }}
        />
      </Typography>
    </Box>
  );

  const linkButtonSx = {
    border: "none",
    background: "none",
    padding: 0,
    cursor: "pointer",
    font: "inherit",
    fontWeight: 600,
    color: "inherit",
    textDecoration: "underline",
  };

  const renderSearchResult = () => {
    switch (search.status) {
      case "searching":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CircularProgress size={14} />
            <Typography sx={subtitleSx}>Searching…</Typography>
          </Box>
        );
      case "not-found":
        return (
          <Box role="status" sx={warningNoticeSx}>
            No user found with that email. Please, try again or{" "}
            <Box component="button" type="button" onClick={() => switchMode("create")} sx={linkButtonSx}>
              create a new user
            </Box>
            .
          </Box>
        );
      case "error":
        return <Typography sx={{ ...subtitleSx, color: collectionColors.danger }}>{search.message}</Typography>;
      case "found": {
        const { user } = search;
        return (
          <Box
            data-testid="found-user"
            sx={{ display: "flex", gap: "12px", padding: "12px", borderRadius: "8px", border: `1px solid ${collectionColors.border}` }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "#E6F4F1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: font,
                fontSize: "12px",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {initialsOf(user.firstName, user.lastName)}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: 0 }}>
              <Typography sx={{ ...fieldLabelSx, lineHeight: "18px" }}>
                {user.firstName} {user.lastName}
              </Typography>
              <Typography sx={{ ...subtitleSx, fontSize: "13px" }}>{user.email}</Typography>
              {user.memberships.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {user.memberships.map((m) => (
                    <Box key={m.organizationId} component="span" sx={organizationChipSx}>
                      {m.organizationName} · {m.role}
                    </Box>
                  ))}
                </Box>
              )}
              {alreadyInScopedOrganization && (
                <Typography sx={{ ...subtitleSx, fontSize: "13px", color: collectionColors.danger }}>
                  Already a member of {scopedOrganization?.name}.
                </Typography>
              )}
            </Box>
          </Box>
        );
      }
      default:
        return null;
    }
  };

  const renderRegisteredNotice = () =>
    registeredUser && (
      <Box role="status" sx={{ ...warningNoticeSx, display: "flex", gap: "12px", alignItems: "flex-start" }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ fontWeight: 600 }}>Email address already registered</Box>
          <Box>
            This email belongs to {registeredUser.firstName} {registeredUser.lastName}.
          </Box>
        </Box>
        <Box component="button" type="button" onClick={() => switchMode("find")} sx={{ ...linkButtonSx, flexShrink: 0 }}>
          Go to find existing user
        </Box>
      </Box>
    );

  // When finding, the access rows stay visible while searching or after a miss, so
  // the layout does not jump; Add stays disabled until someone is found.
  const showRows = mode === "create" || !alreadyInScopedOrganization;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Add user"
      subtitle="Find an existing account by email or create a new user."
      confirmLabel={mode === "find" ? "Add to organization" : "Create user"}
      onConfirm={handleSubmit}
      confirmDisabled={!canSubmit}
      isSubmitting={isSubmitting}
    >
      <Box sx={{ display: "flex", padding: "3px", borderRadius: "12px", background: "#ECEEF2" }}>
        {toggleButton("find", "Find existing user")}
        {toggleButton("create", "Create new user")}
      </Box>

      {mode === "find" ? (
        <>
          {textField(
            "Search by email",
            email,
            setEmail,
            "Enter an email address to find an existing user.",
            "email",
            search.status === "not-found"
          )}
          {renderSearchResult()}
        </>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: "16px" }}>
            {textField("First Name", firstName, setFirstName, "John")}
            {textField("Last Name", lastName, setLastName, "Doe")}
          </Box>
          {textField("Email address", email, setEmail, "name@organization.com", "email", !!registeredUser)}
          {renderRegisteredNotice()}
        </>
      )}

      {showRows && (
        <OrganizationAccessRows
          rows={rows}
          onChange={setRows}
          organizations={organizations}
          excludedOrganizationIds={existingOrganizationIds}
          lockedOrganization={scopedOrganization}
          description={
            mode === "find"
              ? "Select an organization this user does not already belong to."
              : "Choose where this user should have access."
          }
        />
      )}
    </ModalShell>
  );
};

export default AddUserModal;
